import { type JSX, useEffect } from 'react';

export type TierId = 'core' | 'pro' | 'enterprise';

export interface TierDetailModalProps {
  tier: TierId;
  onClose: () => void;
  /**
   * Where the in-modal CTA sends the user. Marketing site passes a
   * Portal signup URL; the Portal-side embed (when it lands) will
   * pass its own handler.
   */
  ctaHref?: string;
  ctaLabel?: string;
}

/**
 * Detail modal for one tier. Opened from the pricing cards via
 * "Learn more" — gives buyers space to read the longer feature
 * list, see the ideal-customer fit, and look at FAQs without
 * leaving the page. Same component can be embedded in the
 * Portal's Upgrade tab (with a different CTA wiring).
 */
export function TierDetailModal({
  tier,
  onClose,
  ctaHref,
  ctaLabel,
}: TierDetailModalProps): JSX.Element {
  const detail = DETAILS[tier];

  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="tier-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tier-modal-title"
      onClick={onClose}
    >
      <div
        className={`tier-modal tier-modal--${tier}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="tier-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <header className="tier-modal__head">
          <span className={`tier-modal__badge tier-modal__badge--${tier}`}>
            {detail.badge}
          </span>
          <h2 id="tier-modal-title" className="tier-modal__title">
            {detail.name}
          </h2>
          <p className="tier-modal__tagline">{detail.tagline}</p>
          <div className="tier-modal__pricing">
            <span className="tier-modal__price">{detail.monthly}</span>
            <span className="tier-modal__price-unit">{detail.priceUnit}</span>
          </div>
          {detail.yearly !== undefined && (
            <p className="tier-modal__yearly">{detail.yearly}</p>
          )}
        </header>

        <section className="tier-modal__section">
          <h3>Built for</h3>
          <ul className="tier-modal__ideal">
            {detail.idealCustomer.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>

        <section className="tier-modal__section">
          <h3>What you get</h3>
          <ul className="tier-modal__features">
            {detail.featureGroups.map((group) => (
              <li key={group.heading} className="tier-modal__feature-group">
                <strong>{group.heading}</strong>
                <p>{group.body}</p>
              </li>
            ))}
          </ul>
        </section>

        {detail.faq.length > 0 && (
          <section className="tier-modal__section">
            <h3>FAQ</h3>
            <ul className="tier-modal__faq">
              {detail.faq.map((item) => (
                <li key={item.q}>
                  <strong>{item.q}</strong>
                  <p>{item.a}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {ctaHref !== undefined && ctaLabel !== undefined && (
          <footer className="tier-modal__foot">
            <a className="tier-modal__cta" href={ctaHref}>
              {ctaLabel}
            </a>
          </footer>
        )}
      </div>
    </div>
  );
}

interface FeatureGroup {
  heading: string;
  body: string;
}
interface Faq {
  q: string;
  a: string;
}
interface TierDetail {
  name: string;
  tagline: string;
  badge: string;
  monthly: string;
  priceUnit: string;
  yearly?: string;
  idealCustomer: string[];
  featureGroups: FeatureGroup[];
  faq: Faq[];
}

const DETAILS: Record<TierId, TierDetail> = {
  core: {
    name: 'Core',
    tagline: 'A working launcher you can run today — no contract, no setup.',
    badge: 'Free',
    monthly: '$0',
    priceUnit: 'forever',
    idealCustomer: [
      'Solo admins or small teams evaluating MobiLauncher on a few devices',
      'IT shops kicking the tires before a Pro/Enterprise rollout',
      'DIY deployments where one profile + one theme is enough',
    ],
    featureGroups: [
      {
        heading: 'Single profile',
        body: 'One launcher profile with your tile grid. App / web / spacer / agent-action tiles all render. Folders included.',
      },
      {
        heading: 'Three-color theme',
        body: 'Background, primary, and accent colors. The full Pro palette is visible in the preview (locked) so you can see what an upgrade unlocks.',
      },
      {
        heading: 'Self-service signup',
        body: 'Provision a tenant slot in 30 seconds. We hand you a launcher URL you can point any kiosk-capable MDM (SOTI MobiControl, Intune, Workspace ONE, Scalefusion, Hexnode, Esper) at — or sideload the standalone APK.',
      },
      {
        heading: 'Built-in error recovery',
        body: 'If the renderer crashes the launcher boots a safe fallback automatically — no support ticket needed to recover the device.',
      },
    ],
    faq: [
      {
        q: "What's the catch?",
        a: 'No catch. Core is free forever for evaluation and small deployments. We charge for the features fleets at scale ask for: theming, multi-profile, scheduling, telemetry. If Core covers your needs, run on it.',
      },
      {
        q: 'Do I need to sign up?',
        a: "Yes — signup creates your tenant slot and gives you a launcher URL. We don't ask for a credit card on Core.",
      },
      {
        q: 'Can I upgrade later?',
        a: 'Yes. The Upgrade tab in the Portal handles the switch via Stripe; your tenant ID and config stay the same.',
      },
    ],
  },
  pro: {
    name: 'Pro',
    tagline: 'For 50–500 device fleets running production.',
    badge: 'Most popular',
    monthly: '$49',
    priceUnit: '/mo per tenant',
    yearly: 'or $499 / yr — save ~15%',
    idealCustomer: [
      'Logistics, field-service, and retail fleets running 50+ devices',
      'Teams running a kiosk-capable MDM (SOTI, Intune, Workspace ONE, Scalefusion, Hexnode, Esper)',
      'Anyone who needs scheduled profiles (day shift / night shift)',
    ],
    featureGroups: [
      {
        heading: 'Full theming',
        body: 'Every color in the launcher palette, custom logo, hero background image, custom font via @font-face. Light/dark mode that follows the OS.',
      },
      {
        heading: 'Multi-profile + scheduled rotation',
        body: 'Run different launchers at different times of day or for different roles. Schedules are cron-style and applied client-side.',
      },
      {
        heading: 'Signage mode (full-screen, no chrome)',
        body: "Hides the launcher's header + footer and scales tiles up for distance viewing. Pair with multi-profile + schedules to auto-cycle through Morning / Lunch / Happy hour menus on a lobby tablet.",
      },
      {
        heading: 'Agent action tiles',
        body: 'Surface MDM-agent actions (reboot, clear app data, sync now) as tap-to-run tiles. The SOTI MobiControl JS Agent integration ships first; the dispatch path is MDM-agnostic so other agents land per customer ask.',
      },
      {
        heading: 'Broadcasts to your fleet',
        body: 'Post a short message (info / warning / critical) from the Portal. Devices pick it up on their next poll and render it in the bell-icon panel.',
      },
      {
        heading: 'Exit gesture + bcrypt PIN',
        body: 'Long-press corner unlocks an admin exit dialog secured by a per-tenant PIN. Stays kiosked unless you really want out.',
      },
      {
        heading: 'Diagnostic overlay',
        body: 'Long-press another corner to surface engine state, last sync, battery, network — without needing remote console access.',
      },
      {
        heading: 'Offline-first ZIP bundle',
        body: 'Download a bundle that includes the renderer + license + assets. Your MDM pushes it once and the device works offline — no first-launch network round-trip.',
      },
    ],
    faq: [
      {
        q: 'Is there a setup fee?',
        a: "No — the monthly or yearly price is all-in. Pro Services (training, hands-on rollout help) is a separate optional add-on if you want it.",
      },
      {
        q: 'Per device or per tenant?',
        a: 'Per tenant. One subscription covers all the devices running on that tenant — we don\'t per-seat-charge.',
      },
      {
        q: 'How do schedules work?',
        a: 'Configure profiles + cron-style schedules in the Builder. The launcher rotates client-side — no server round trip on the boundary. Falls back to the default profile if a schedule expression fails to parse.',
      },
      {
        q: 'Can I downgrade?',
        a: 'Yes, via the Billing Portal (Manage subscription button on the Upgrade tab). Pro features stop applying at the end of the billing period.',
      },
    ],
  },
  enterprise: {
    name: 'Enterprise',
    tagline: 'For larger fleets, ruggedized hardware, and compliance shops.',
    badge: 'Enterprise',
    monthly: '$199',
    priceUnit: '/mo per tenant',
    yearly: 'or $1,999 / yr — save ~16%',
    idealCustomer: [
      'Larger fleets on rugged Android (Zebra, Samsung Galaxy XCover/Tab Active, Honeywell, and others — device-family certification happens during beta onboarding)',
      'Compliance-heavy shops needing SSO display + audit log',
      'Operators running signage / kiosk modes alongside dispatch',
    ],
    featureGroups: [
      {
        heading: 'Everything in Pro',
        body: 'All theming, multi-profile, broadcasts, exit gesture, diagnostic overlay, agent actions, and offline bundle features ship with Enterprise.',
      },
      {
        heading: 'Remote config push',
        body: 'Live config updates without redeploying. Server stamps an ETag; devices poll and only apply changes when the hash differs. Includes last-known-good fallback so a bad push never bricks the fleet.',
      },
      {
        heading: 'Telemetry export',
        body: 'Device-side telemetry pipeline with retry, age-out, and the customer\'s choice of endpoint. Counts, errors, agent dispatches — exported on a schedule.',
      },
      {
        heading: 'SSO display',
        body: "Surface the SSO display name (from the device's MDM agent) in the launcher's status bar so shared devices show who's currently signed in.",
      },
      {
        heading: 'Audit log',
        body: 'Every config change is logged with operator + timestamp. Helpful for compliance audits and incident retros.',
      },
      {
        heading: 'Priority support',
        body: 'Dedicated Slack channel with our engineering team. Same-business-day response on production incidents.',
      },
    ],
    faq: [
      {
        q: 'Can we self-host?',
        a: "The renderer ships as a static bundle (offline-first), but the config + license server is hosted by us on Cloudflare. Self-hosting the server is a Pro Services engagement — happy to scope it.",
      },
      {
        q: 'What about HIPAA / SOC2?',
        a: "Cloudflare's infrastructure is SOC2 / ISO 27001 compliant. We don't process PHI in the launcher today; if your use case needs HIPAA-grade controls, talk to sales — we can scope a deployment that meets your audit requirements.",
      },
      {
        q: 'How does Pro Services work?',
        a: "Hands-on engagement to get the fleet wired into your kiosk-capable MDM (SOTI MobiControl, Intune, Workspace ONE, Scalefusion, Hexnode, Esper), devices flashed, admins trained. Packages run from 5 hours (Jumpstart) to 40 hours (Enterprise rollout). Effective hourly drops on larger packages.",
      },
    ],
  },
};
